import type { FilterDefinition, UnifiedFilterOperator } from '@/components/filters/list-filter-types'
import {
  formatCommaKeywordCommitHint,
  formatCommaKeywordDisplay,
  formatTextFilterOperatorChip,
  matchesCommaKeywordFilter,
  matchesUnifiedListFilter,
  normalizeCommaSeparatedTextQuery,
  parseCommaList,
  parseUnifiedFilterOperator,
  serializeCommaList,
} from '@/lib/list-filters/list-filter-params'
import { buildUnifiedMultiFilterDef } from '@/lib/list-filters/build-unified-filter-def'
import {
  UNIFIED_EMPLOYMENT_FILTER_OPTIONS,
  UNIFIED_WORK_MODE_FILTER_OPTIONS,
} from '@/lib/list-filters/job-meta-filter-options'
import {
  EMPLOYMENT_TYPE_LABELS,
  PUBLICATION_STATUS_LABELS,
  WORK_MODE_LABELS,
} from '@/utils/job-meta-labels'
import type { Opportunity } from '@/hooks/use-opportunities'
import { appendListPageParams, DEFAULT_LIST_PAGE_SIZE } from '@/lib/list-pagination'
import type {
  EmployerJobListFilters,
  EmployerJobsFilterCtx,
  EmployerJobsSort,
  EmployerJobsTextOperator,
  PublicationStatusFilter,
} from '@/lib/list-filters/employer-jobs-types'
import {
  EMPLOYER_JOBS_FILTER_ICONS,
  STATUS_FILTER_OPTIONS,
} from '@/lib/list-filters/employer-jobs-constants'

export type {
  EmployerJobListFilters,
  EmployerJobsFilterCtx,
  EmployerJobsSort,
  EmployerJobsTextOperator,
  PublicationStatusFilter,
} from '@/lib/list-filters/employer-jobs-types'

export {
  EMPLOYER_JOBS_DEFAULT_OPERATORS,
  EMPLOYER_JOBS_SORT_LABELS,
  EMPLOYMENT_FILTER_OPTIONS,
  SORT_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  WORK_MODE_FILTER_OPTIONS,
} from '@/lib/list-filters/employer-jobs-constants'

const PUBLICATION_STATUS_SET = new Set<string>(Object.keys(PUBLICATION_STATUS_LABELS))
const EMPLOYMENT_TYPE_SET = new Set<string>(Object.keys(EMPLOYMENT_TYPE_LABELS))
const WORK_MODE_SET = new Set<string>(Object.keys(WORK_MODE_LABELS))

const EMPLOYER_JOBS_FILTER_QUERY_KEYS = [
  'q',
  'q_op',
  'status',
  'status_op',
  'employment',
  'employment_op',
  'work_mode',
  'work_mode_op',
  'sort',
  'page',
] as const

/** Parse comma-separated list filter values from URL params. */
export function parseEmployerJobsListParam(raw: string | null, allowed?: Set<string>): string[] {
  return parseCommaList(raw, allowed)
}

export const parseEmployerJobsOperator = parseUnifiedFilterOperator

export function parseEmployerJobsTextOperator(value: string | null): EmployerJobsTextOperator {
  return value === 'does_not_contain' ? 'does_not_contain' : 'contains'
}

export function formatEmployerJobsTextOperator(operator: EmployerJobsTextOperator): string {
  return formatTextFilterOperatorChip(operator)
}

export function parseEmployerJobsKeywordTerms(q: string): string[] {
  return parseCommaList(q)
}

export function normalizeEmployerJobsKeywordQuery(q: string): string {
  return normalizeCommaSeparatedTextQuery(q)
}

export const formatEmployerJobsKeywordDisplay = formatCommaKeywordDisplay

export const formatEmployerJobsKeywordCommitHint = formatCommaKeywordCommitHint

export const serializeEmployerJobsListParam = serializeCommaList

export const matchesEmployerJobsMultiFilter = matchesUnifiedListFilter

export type EmployerJobListFilterCriteria = Pick<
  EmployerJobListFilters,
  | 'q'
  | 'q_operator'
  | 'publication_status'
  | 'publication_status_operator'
  | 'employment_type'
  | 'employment_type_operator'
  | 'work_mode'
  | 'work_mode_operator'
>

export type EmployerJobKeywordMatchContext = {
  keywordTerms: string[]
  q_operator: EmployerJobsTextOperator
}

export function buildEmployerJobListFilterCriteria(
  filters: EmployerJobListFilterCriteria,
): EmployerJobListFilterCriteria & EmployerJobKeywordMatchContext {
  return {
    ...filters,
    keywordTerms: parseEmployerJobsKeywordTerms(filters.q),
  }
}

function employerJobKeywordHaystack(opp: {
  title: string
  description: string
  location?: string | null
}): string {
  return [opp.title, opp.description, opp.location || ''].join('\n').toLowerCase()
}

export function matchesEmployerJobsKeywordFilter(
  opp: {
    title: string
    description: string
    location?: string | null
  },
  criteria: EmployerJobKeywordMatchContext,
): boolean {
  return matchesCommaKeywordFilter(
    employerJobKeywordHaystack(opp),
    criteria.keywordTerms,
    criteria.q_operator,
  )
}

/**
 * Default (no status filter) hides archived. An explicit status filter — is or
 * is_not — widens the view to archived jobs deliberately.
 */
function matchesPublicationStatusFilter(
  selected: PublicationStatusFilter[],
  operator: UnifiedFilterOperator,
  actual: string,
): boolean {
  if (selected.length === 0) return actual !== 'archived'
  return matchesUnifiedListFilter(selected, actual, operator)
}

export function getEmployerJobsFilterDefinitions(): FilterDefinition<
  EmployerJobListFilters,
  EmployerJobsFilterCtx
>[] {
  return [
    buildUnifiedMultiFilterDef({
      key: 'publication_status',
      label: 'Status',
      icon: EMPLOYER_JOBS_FILTER_ICONS.publication_status,
      options: STATUS_FILTER_OPTIONS,
      labels: PUBLICATION_STATUS_LABELS,
      excludeOptionValues: ['all'],
      getValues: (filters) => filters.publication_status,
      setValues: (filters, values) => ({
        ...filters,
        publication_status: values as PublicationStatusFilter[],
        page: 1,
      }),
    }),
    buildUnifiedMultiFilterDef({
      key: 'employment_type',
      label: 'Employment',
      icon: EMPLOYER_JOBS_FILTER_ICONS.employment_type,
      options: UNIFIED_EMPLOYMENT_FILTER_OPTIONS,
      labels: EMPLOYMENT_TYPE_LABELS,
      getValues: (filters) => filters.employment_type,
      setValues: (filters, values) => ({ ...filters, employment_type: values, page: 1 }),
    }),
    buildUnifiedMultiFilterDef({
      key: 'work_mode',
      label: 'Work mode',
      icon: EMPLOYER_JOBS_FILTER_ICONS.work_mode,
      options: UNIFIED_WORK_MODE_FILTER_OPTIONS,
      labels: WORK_MODE_LABELS,
      getValues: (filters) => filters.work_mode,
      setValues: (filters, values) => ({ ...filters, work_mode: values, page: 1 }),
    }),
  ]
}

export function parseEmployerJobsSort(value: string | null): EmployerJobsSort {
  if (value === 'updated_asc' || value === 'title_asc' || value === 'title_desc') return value
  return 'updated_desc'
}

export function parseEmployerJobsFiltersFromSearchParams(
  searchParams: URLSearchParams,
): EmployerJobListFilters {
  return {
    q: searchParams.get('q') || '',
    q_operator: parseEmployerJobsTextOperator(searchParams.get('q_op')),
    page: parseInt(searchParams.get('page') || '1', 10) || 1,
    page_size: DEFAULT_LIST_PAGE_SIZE,
    publication_status: parseEmployerJobsListParam(
      searchParams.get('status'),
      PUBLICATION_STATUS_SET,
    ) as PublicationStatusFilter[],
    publication_status_operator: parseEmployerJobsOperator(searchParams.get('status_op')),
    employment_type: parseEmployerJobsListParam(searchParams.get('employment'), EMPLOYMENT_TYPE_SET),
    employment_type_operator: parseEmployerJobsOperator(searchParams.get('employment_op')),
    work_mode: parseEmployerJobsListParam(searchParams.get('work_mode'), WORK_MODE_SET),
    work_mode_operator: parseEmployerJobsOperator(searchParams.get('work_mode_op')),
    sort: parseEmployerJobsSort(searchParams.get('sort')),
  }
}

export function applyEmployerJobsFiltersToSearchParams(
  base: URLSearchParams,
  filters: EmployerJobListFilters,
): URLSearchParams {
  const next = new URLSearchParams(base)
  for (const key of EMPLOYER_JOBS_FILTER_QUERY_KEYS) {
    next.delete(key)
  }

  if (filters.q) {
    next.set('q', filters.q)
    if (filters.q_operator === 'does_not_contain') {
      next.set('q_op', 'does_not_contain')
    }
  }
  if (filters.publication_status.length > 0) {
    next.set('status', serializeEmployerJobsListParam(filters.publication_status))
    if (filters.publication_status_operator === 'is_not') {
      next.set('status_op', 'is_not')
    }
  }
  if (filters.employment_type.length > 0) {
    next.set('employment', serializeEmployerJobsListParam(filters.employment_type))
    if (filters.employment_type_operator === 'is_not') {
      next.set('employment_op', 'is_not')
    }
  }
  if (filters.work_mode.length > 0) {
    next.set('work_mode', serializeEmployerJobsListParam(filters.work_mode))
    if (filters.work_mode_operator === 'is_not') {
      next.set('work_mode_op', 'is_not')
    }
  }
  if (filters.sort !== 'updated_desc') next.set('sort', filters.sort)
  if (filters.page > 1) next.set('page', filters.page.toString())

  return next
}

export function matchesEmployerJobListFilters(
  opp: {
    publication_status?: string | null
    employment_type?: string | null
    work_mode?: string | null
    title: string
    description: string
    location?: string | null
  },
  criteria: EmployerJobListFilterCriteria & Partial<EmployerJobKeywordMatchContext>,
): boolean {
  if (
    !matchesPublicationStatusFilter(
      criteria.publication_status,
      criteria.publication_status_operator,
      opp.publication_status || '',
    )
  ) {
    return false
  }
  if (
    criteria.employment_type.length > 0 &&
    !matchesEmployerJobsMultiFilter(
      criteria.employment_type,
      opp.employment_type || '',
      criteria.employment_type_operator,
    )
  ) {
    return false
  }
  if (
    criteria.work_mode.length > 0 &&
    !matchesEmployerJobsMultiFilter(
      criteria.work_mode,
      opp.work_mode || '',
      criteria.work_mode_operator,
    )
  ) {
    return false
  }

  const keywordContext: EmployerJobKeywordMatchContext = {
    keywordTerms: criteria.keywordTerms ?? parseEmployerJobsKeywordTerms(criteria.q),
    q_operator: criteria.q_operator,
  }
  if (keywordContext.keywordTerms.length > 0 && !matchesEmployerJobsKeywordFilter(opp, keywordContext)) {
    return false
  }

  return true
}

function opportunitySortTime(opportunity: Pick<Opportunity, 'updated_at' | 'created_at'>): number {
  const raw = opportunity.updated_at || opportunity.created_at
  if (!raw) return 0
  const timestamp = Date.parse(raw)
  return Number.isFinite(timestamp) ? timestamp : 0
}

export function compareEmployerJobs(
  a: Pick<Opportunity, 'title' | 'updated_at' | 'created_at'>,
  b: Pick<Opportunity, 'title' | 'updated_at' | 'created_at'>,
  sort: EmployerJobsSort,
): number {
  switch (sort) {
    case 'title_asc':
      return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
    case 'title_desc':
      return b.title.localeCompare(a.title, undefined, { sensitivity: 'base' })
    case 'updated_asc':
      return opportunitySortTime(a) - opportunitySortTime(b)
    case 'updated_desc':
    default:
      return opportunitySortTime(b) - opportunitySortTime(a)
  }
}

/** Query params for GET …/organisations/managed/{slug}/opportunities/ */
export function buildEmployerJobsApiSearchParams(
  filters: EmployerJobListFilters,
  options?: { page?: number; pageSize?: number; includePagination?: boolean },
): URLSearchParams {
  const params = new URLSearchParams()
  if (filters.q) {
    params.set('q', filters.q)
    if (filters.q_operator === 'does_not_contain') params.set('q_op', 'does_not_contain')
  }
  if (filters.publication_status.length > 0) {
    params.set('status', serializeEmployerJobsListParam(filters.publication_status))
    if (filters.publication_status_operator === 'is_not') params.set('status_op', 'is_not')
  }
  if (filters.employment_type.length > 0) {
    params.set('employment', serializeEmployerJobsListParam(filters.employment_type))
    if (filters.employment_type_operator === 'is_not') params.set('employment_op', 'is_not')
  }
  if (filters.work_mode.length > 0) {
    params.set('work_mode', serializeEmployerJobsListParam(filters.work_mode))
    if (filters.work_mode_operator === 'is_not') params.set('work_mode_op', 'is_not')
  }
  if (filters.sort !== 'updated_desc') params.set('sort', filters.sort)
  if (options?.includePagination !== false) {
    appendListPageParams(
      params,
      options?.page ?? filters.page,
      options?.pageSize ?? filters.page_size,
    )
  }
  return params
}
